/**
 * REQUESTS (EXPERT-ROOMS.md §6, E8 X4) — what this room asked expert teams for (status + every step), and — in an
 * expert team's room — what it was asked (a department: its own queue; the Lead: the whole team's), with Approve /
 * Reject → Start (the request runs HERE as typed input) → Mark done / failed (when a run reported nothing) → Grant the
 * access the run proposed to the requesting room (X5). What this room was GRANTED shows read-only. The server decides
 * who may act; a refusal says why. Additive section.
 */
import React, { useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const TONE = { submitted: 'bg-amber-100 text-amber-900', approved: 'bg-blue-100 text-blue-900',
  'in-progress': 'bg-indigo-100 text-indigo-900', done: 'bg-emerald-100 text-emerald-900',
  rejected: 'bg-rose-100 text-rose-900', failed: 'bg-rose-100 text-rose-900', cancelled: 'bg-gray-100 text-gray-600' };

function Values({ values }) {
  const e = Object.entries(values || {});
  if (!e.length) return null;
  return (
    <dl className="mt-0.5 grid grid-cols-[8rem_1fr] gap-x-2 text-[11px] text-gray-700">
      {e.map(([k, v]) => (
        <React.Fragment key={k}><dt className="text-gray-500">{k}</dt><dd>{Array.isArray(v) ? v.join(', ') : String(v)}</dd></React.Fragment>
      ))}
    </dl>
  );
}

function Row({ r, side, onAct, onStart }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [showHist, setShowHist] = useState(false);
  const act = async (action, extra = {}) => {
    setBusy(true); setErr('');
    try { await onAct({ action, expertNetworkId: r.expertNetworkId, requestId: r.requestId, ...extra }); } catch (e) { setErr(e?.message || "Couldn't do that."); }
    setBusy(false);
  };
  const start = async () => {
    setBusy(true); setErr('');
    try { await onStart(r); } catch (e) { setErr(e?.message || "Couldn't start it."); }
    setBusy(false);
  };
  const proposed = (r.proposedGrants || []).filter((g) => !(r.granted || []).some((x) => x.capability === g.capability));
  const who = side === 'queue' ? `from ${r.requester?.name || 'a room'} (${r.requester?.poolId || ''})`
    : `to ${r.team || r.expertName}${r.departmentRoom ? ` (${r.departmentRoom})` : ''}`;
  return (
    <div className="rounded-md border border-gray-200 px-2 py-1.5" data-testid={`request-${r.requestId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium text-gray-900">{r.offeringTitle}</span>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${TONE[r.status] || ''}`}>{r.status}</span>
        <span className="text-[10px] text-gray-500">{who}</span>
      </div>
      {r.note ? <p className="mt-0.5 text-[11px] text-gray-600">{r.note}</p> : null}
      {r.summary ? <p className="mt-0.5 text-[11px] text-gray-800" data-testid={`request-summary-${r.requestId}`}>{r.summary}</p> : null}
      <Values values={r.values} />
      <div className="mt-1 flex flex-wrap gap-2">
        {side === 'queue' && r.status === 'submitted' ? (
          <>
            <button type="button" disabled={busy} className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`}
              onClick={() => act('approve')} data-testid={`request-approve-${r.requestId}`}>Approve</button>
            <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
              onClick={() => act('reject')} data-testid={`request-reject-${r.requestId}`}>Reject</button>
          </>
        ) : null}
        {side === 'queue' && r.status === 'approved' && onStart && r.fulfilmentPrompt ? (
          <button type="button" disabled={busy} className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
            onClick={start} data-testid={`request-start-${r.requestId}`}>Start</button>
        ) : null}
        {side === 'queue' && r.status === 'in-progress' ? (
          <>
            <button type="button" disabled={busy} className={`${btn} text-emerald-700 hover:bg-emerald-50`}
              onClick={() => act('complete', { status: 'done' })} data-testid={`request-done-${r.requestId}`}>Mark done</button>
            <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
              onClick={() => act('complete', { status: 'failed' })}>Mark failed</button>
          </>
        ) : null}
        {side === 'queue' && r.status === 'done' && proposed.length ? proposed.map((g) => (
          <button key={g.capability} type="button" disabled={busy || !g.connectionId}
            className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50`}
            onClick={() => act('grant', { grants: [g] })} data-testid={`request-grant-${r.requestId}-${g.capability}`}>
            Grant {g.capability}{g.connectionId ? ` (${g.connectionId})` : ''}</button>
        )) : null}
        {side === 'outgoing' && (r.status === 'submitted' || r.status === 'approved') ? (
          <button type="button" disabled={busy} className={`${btn} text-gray-600 hover:bg-gray-100`}
            onClick={() => act('cancel')} data-testid={`request-cancel-${r.requestId}`}>Cancel</button>
        ) : null}
        <button type="button" className={`${btn} text-gray-500 hover:bg-gray-100`} onClick={() => setShowHist((v) => !v)}>
          {showHist ? 'Hide history' : 'History'}</button>
      </div>
      {showHist ? (
        <ul className="mt-1 space-y-0.5 text-[10px] text-gray-600" data-testid={`request-history-${r.requestId}`}>
          {(r.history || []).map((h, i) => (
            <li key={i}>{h.at?.slice(0, 16).replace('T', ' ')} · {h.by} · {h.status}{h.note ? ` — ${h.note}` : ''}</li>
          ))}
        </ul>
      ) : null}
      {err ? <p className="text-[11px] text-rose-600">{err}</p> : null}
    </div>
  );
}

export default function RoomExpertRequests({ value, onAct, onStart = null }) {
  const v = value || {};
  const queue = Array.isArray(v.queue) ? v.queue : null;
  return (
    <div className="space-y-3 text-[12px]" data-testid="room-expert-requests">
      {(v.grants || []).length ? (
        <div data-testid="requests-grants">
          <p className="text-[11px] font-medium text-gray-900">Granted to this room</p>
          {v.grants.map((g) => (
            <p key={g.grantId} className="mt-0.5 text-[11px] text-gray-700">
              {g.capability} · <span className="font-mono">{g.connectionId}</span> — granted by the {g.team || 'expert'} team
              (only they change it)</p>
          ))}
        </div>
      ) : null}
      {queue ? (
        <div data-testid="requests-queue">
          <p className="text-[11px] font-medium text-gray-900">
            Asked of {v.queueScope === 'team' ? 'the team' : `the ${v.queueScope} team`}</p>
          {queue.length ? queue.map((r) => <div key={r.requestId} className="mt-1"><Row r={r} side="queue" onAct={onAct} onStart={onStart} /></div>)
            : <p className="text-[11px] text-gray-500">Nothing asked yet.</p>}
        </div>
      ) : null}
      {(v.outgoing || []).length ? (
        <div data-testid="requests-outgoing">
          <p className="text-[11px] font-medium text-gray-900">Asked by this room</p>
          {v.outgoing.map((r) => <div key={r.requestId} className="mt-1"><Row r={r} side="outgoing" onAct={onAct} /></div>)}
        </div>
      ) : null}
    </div>
  );
}
